/* eslint-disable import/no-relative-packages -- the widget is a separate package with no workspace link, so the protocol it speaks is read in place */
import { useLayoutEffect, useRef } from 'react';
import {
  BackdropMotion,
  BackdropShape,
  cornerRadii,
  MotionFrame,
  motionStartTime,
} from '../../../../../widget/src/lib/backdropProtocol';

const px = (values: number[]): string => values.map((value) => `${value}px`).join(' ');

const motionKeyframe = (frame: MotionFrame): Keyframe => ({
  top: `${frame.top}px`,
  left: `${frame.left}px`,
  width: `${frame.width}px`,
  height: `${frame.height}px`,
  borderRadius: px(frame.radii),
  clipPath: `inset(${px(frame.inset)})`,
});

type Props = {
  shape: BackdropShape;
  blur: number;
  motion: BackdropMotion | null;
  onMotionEnd: (motion: BackdropMotion) => void;
};

/** Blurs the screenshot behind one glass surface, as chat.js does behind the real widget. */
export const PreviewBackdropLayer = ({ shape, blur, motion, onMotionEnd }: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  // Played on this page's clock from the widget's start time, so both draw the same frame together.
  useLayoutEffect(() => {
    const layer = ref.current;
    if (!motion || !layer || typeof layer.animate !== 'function') return;
    let animation: Animation;
    try {
      animation = layer.animate([motionKeyframe(motion.from), motionKeyframe(motion.to)], {
        duration: motion.duration,
        easing: motion.easing,
        fill: 'both',
      });
    } catch {
      // An easing this browser can't parse: the per-frame shapes still follow the window.
      return;
    }
    animation.startTime = motionStartTime(motion);
    animation.onfinish = () => onMotionEnd(motion);
    return () => animation.cancel();
  }, [motion, onMotionEnd]);

  const filter = `blur(${blur}px) saturate(160%)`;
  return (
    <div
      ref={ref}
      aria-hidden
      data-testid="widget-preview-backdrop"
      className="pointer-events-none absolute"
      style={{
        top: shape.top,
        left: shape.left,
        width: shape.width,
        height: shape.height,
        borderRadius: cornerRadii(shape),
        opacity: Number.isFinite(shape.opacity) ? shape.opacity : 1,
        backdropFilter: filter,
        WebkitBackdropFilter: filter,
      }}
    />
  );
};
